import { Plus, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import jobService from "../../../services/JobService";

interface RunJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobName: string;
  onSuccess: () => void;
}

const RunJobModal: React.FC<RunJobModalProps> = ({ isOpen, onClose, jobName, onSuccess }) => {
  const [envVars, setEnvVars] = useState<{ name: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { name: "", value: "" }]);
  };

  const handleRemoveEnvVar = (index: number) => {
    const newEnvVars = [...envVars];
    newEnvVars.splice(index, 1);
    setEnvVars(newEnvVars);
  };

  const handleEnvVarChange = (index: number, field: "name" | "value", value: string) => {
    const newEnvVars = [...envVars];
    newEnvVars[index][field] = value;
    setEnvVars(newEnvVars);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Filter out empty env vars
      const filteredEnvVars = envVars.filter(v => v.name.trim() !== "");
      const response = await jobService.runJob(jobName, filteredEnvVars);
      setSuccessMessage(response.message || "Job triggered successfully");
      setEnvVars([]);
    } catch (err: any) {
      setError(err.message || "Failed to run job");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (successMessage) {
      onSuccess(); // Trigger refresh only on success
    }
    setSuccessMessage(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-rp-surface rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b dark:border-rp-separator">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-rp-grey">
            {successMessage ? "Job Triggered" : `Run Job: ${jobName}`}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-rp-grey/50 dark:hover:text-rp-grey"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {successMessage ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-center text-green-500 mb-4">
              <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-gray-900 dark:text-rp-grey mb-2">{successMessage}</p>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                <p className="font-semibold mb-1">Note:</p>
                It may take up to 2 minutes for the job execution to appear in the history list.
              </div>
            </div>
            <div className="flex justify-center pt-4">
              <button
                onClick={handleClose}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-100 rounded-lg dark:bg-red-900/30 dark:text-red-400">
                {error}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-rp-grey/70">Environment Variables</label>
                <button
                  type="button"
                  onClick={handleAddEnvVar}
                  className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Variable
                </button>
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                {envVars.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-rp-grey/50 italic">No environment variables added.</p>
                )}
                {envVars.map((envVar, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={envVar.name}
                      onChange={e => handleEnvVarChange(index, "name", e.target.value)}
                      className="flex-1 w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 dark:bg-rp-surface dark:border-rp-separator dark:text-rp-grey focus:ring-2 focus:ring-teal-500 focus:outline-none"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={envVar.value}
                      onChange={e => handleEnvVarChange(index, "value", e.target.value)}
                      className="flex-1 w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 dark:bg-rp-surface dark:border-rp-separator dark:text-rp-grey focus:ring-2 focus:ring-teal-500 focus:outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveEnvVar(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg dark:hover:bg-red-900/20 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t dark:border-rp-separator">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 mr-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-rp-surface dark:text-rp-grey/70 dark:border-rp-separator dark:hover:bg-rp-surface-raised"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Starting..." : "Run Job"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RunJobModal;
